import React from 'react';
import { CreditAnalytics, AccountInformation, CreditScore } from '../../types/api';
import { formatDate } from '../../lib/utils';
import { PaymentHistoryLegend } from './PaymentHistoryLegend';

interface CreditReportViewProps {
  analytics: CreditAnalytics;
}

export function CreditReportView({ analytics }: CreditReportViewProps) {
  const creditScores = analytics?.fullReport?.creditScores || [];
  const accounts = analytics?.fullReport?.accountInformation || [];

  const getCreditRating = (score: string) => {
    const numScore = parseInt(score);
    if (numScore >= 800) return 'Excellent';
    if (numScore >= 740) return 'Very Good';
    if (numScore >= 670) return 'Good';
    if (numScore >= 580) return 'Fair';
    return 'Poor';
  };

  const formatCurrency = (amount: string) => {
    if (!amount || amount === '0' || amount === '$0') return '$0.00';
    const numAmount = parseFloat(amount.replace(/[$,]/g, ''));
    if (isNaN(numAmount)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  const formatDateForDisplay = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatMonthYear = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Helper: Extract visible digits from account number
  const getVisibleDigits = (accountNumber: string): string => {
    return accountNumber.replace(/\*/g, '');
  };

  // Helper: Check if two account numbers could be the same account
  // Stricter matching to avoid false positives like grouping "53917610****", "05**", "51740220****"
  const couldBeSameAccount = (num1: string, num2: string): boolean => {
    const visible1 = getVisibleDigits(num1);
    const visible2 = getVisibleDigits(num2);
    
    // If either is empty, can't match
    if (!visible1 || !visible2) return false;
    
    const longer = visible1.length >= visible2.length ? visible1 : visible2;
    const shorter = visible1.length >= visible2.length ? visible2 : visible1;
    
    // Get positions of asterisks to understand truncation pattern
    const hasLeadingAsterisks1 = num1.startsWith('*');
    const hasTrailingAsterisks1 = num1.endsWith('*');
    const hasLeadingAsterisks2 = num2.startsWith('*');
    const hasTrailingAsterisks2 = num2.endsWith('*');
    
    // Rule 1: If one number is completely contained in another AND they have at least 4 matching digits
    // This handles cases like "53210411****" and "532104****"
    if (longer.includes(shorter) && shorter.length >= 4) {
      // Check if the position makes sense based on truncation pattern
      const position = longer.indexOf(shorter);
      
      // If shorter has leading asterisks, it should match at the end of longer
      if (hasLeadingAsterisks2 && hasTrailingAsterisks1) {
        return longer.endsWith(shorter);
      }
      // If shorter has trailing asterisks, it should match at the start of longer
      if (hasTrailingAsterisks2 && hasTrailingAsterisks1) {
        return longer.startsWith(shorter);
      }
      
      // Otherwise, accept any substring match if long enough
      return true;
    }
    
    // Rule 2: For very short visible digits (< 4), only match if they're at the START or END
    if (shorter.length < 4) {
      // Only match if it's a prefix or suffix match
      if (hasTrailingAsterisks2 && hasTrailingAsterisks1) {
        return longer.startsWith(shorter);
      }
      if (hasLeadingAsterisks2 && hasLeadingAsterisks1) {
        return longer.endsWith(shorter);
      }
      return false; // Don't match short substrings in the middle
    }
    
    // Rule 3: Check for overlap at boundaries (at least 4 digits must overlap)
    const minOverlap = Math.min(4, shorter.length);
    for (let overlapLen = Math.min(visible1.length, visible2.length); overlapLen >= minOverlap; overlapLen--) {
      // Check if end of num1 matches start of num2
      if (visible1.slice(-overlapLen) === visible2.slice(0, overlapLen)) {
        return true;
      }
      // Check if end of num2 matches start of num1
      if (visible2.slice(-overlapLen) === visible1.slice(0, overlapLen)) {
        return true;
      }
    }
    
    return false;
  };

  // Group accounts by account number matching
  const groupedAccounts: Record<string, { creditorName: string; accounts: AccountInformation[] }> = {};
  let groupCounter = 0;
  
  accounts.forEach(account => {
    // Find existing group where account number could match
    let matchedKey: string | null = null;
    
    for (const [key, group] of Object.entries(groupedAccounts)) {
      // Check if any account in the group has a matching account number
      const hasMatchingAccount = group.accounts.some(existingAccount => 
        couldBeSameAccount(account.accountNumber, existingAccount.accountNumber)
      );
      
      if (hasMatchingAccount) {
        matchedKey = key;
        break;
      }
    }
    
    if (matchedKey) {
      // Add to existing group
      groupedAccounts[matchedKey].accounts.push(account);
    } else {
      // Create new group
      const visibleDigits = getVisibleDigits(account.accountNumber);
      const key = `account_${visibleDigits}_${groupCounter++}`;
      groupedAccounts[key] = {
        creditorName: account.creditorName,
        accounts: [account],
      };
    }
  });

  const generatePaymentHistoryGrid = (account: AccountInformation) => {
    // Generate 24 months of data (current month back to 23 months ago)
    const months = [];
    const years = [];
    const currentDate = new Date();
    
    for (let i = 23; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push(date.toLocaleDateString('en-US', { month: 'short' }));
      years.push(date.getFullYear().toString().slice(-2));
    }

    // Map payment history to the grid
    const statuses = new Array(24).fill('NO_DATA'); // White box - not present in response
    
    if (account.paymentHistory && account.paymentHistory.length > 0) {
      account.paymentHistory.forEach(entry => {
        const entryDate = new Date(entry.date);
        const monthsBack = (currentDate.getFullYear() - entryDate.getFullYear()) * 12 + 
                          (currentDate.getMonth() - entryDate.getMonth());
        
        if (monthsBack >= 0 && monthsBack < 24) {
          const index = 23 - monthsBack;
          let status = entry.status;
          
          // Current or C = OK (green)
          if (status === 'Current' || status === 'OK' || status === 'C') {
            status = 'OK';
          }
          // Unknown or U = Gray
          else if (status === 'U' || status === 'Unknown' || status === '' || !status) {
            status = 'UNKNOWN';
          }
          // Late payments
          else if (status.includes('30')) {
            status = '30';
          } else if (status.includes('60')) {
            status = '60';
          } else if (status.includes('90')) {
            status = '90';
          } else if (status.includes('120')) {
            status = '120';
          } else if (status.includes('150')) {
            status = '150';
          } else if (status.includes('180')) {
            status = '180';
          }
          // Codes
          else if (status === 'CO' || status.toLowerCase().includes('chargeoff') || status.toLowerCase().includes('collection')) {
            status = 'CO';
          } else if (status === 'RF' || status.toLowerCase().includes('repossession') || status.toLowerCase().includes('foreclosure')) {
            status = 'RF';
          } else if (status === 'PP' || status.toLowerCase().includes('payment plan')) {
            status = 'PP';
          } else if (status === 'VS' || status.toLowerCase().includes('voluntary surrender')) {
            status = 'VS';
          }
          
          statuses[index] = status;
        }
      });
    }

    return { months, years, statuses };
  };

  return (
    <div className="space-y-6">
      <style>{`
        .credit-report-container {
          background: white;
          padding: 0;
        }
        
        .account-section {
          margin-bottom: 32px;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .creditor-header {
          background: #3B4752;
          color: white;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .account-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          table-layout: fixed;
        }
        
        .account-table col:first-child {
          width: 25%;
        }
        
        .account-table col:not(:first-child) {
          width: 25%;
        }
        
        .account-table thead tr {
          background: transparent;
        }
        
        .account-table th {
          padding: 12px 16px;
          font-weight: 600;
          text-align: center;
          border: none;
          color: white;
          font-size: 13px;
        }
        
        .account-table th:first-child {
          background: transparent;
          color: #374151;
        }
        
        .account-table th.header-transunion {
          background: #4AA3C6;
        }
        
        .account-table th.header-experian {
          background: #2B4E8C;
        }
        
        .account-table th.header-equifax {
          background: #8C2A35;
        }
        
        .account-table tbody tr {
          border: none;
        }
        
        .account-table tbody tr:nth-child(even) {
          background: #F9FAFB;
        }
        
        .account-table tbody tr:nth-child(odd) {
          background: white;
        }
        
        .account-table td {
          padding: 6px 16px;
          color: #374151;
          border: none;
        }
        
        .account-table td:first-child {
          font-weight: 500;
          text-align: right;
          color: #374151;
          border: none;
          white-space: nowrap;
        }
        
        .account-table td:not(:first-child) {
          text-align: center;
        }
        
        .payment-history-section {
          margin-top: 24px;
          padding: 0 8px 16px 8px;
        }
        
        .payment-history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding: 0 8px;
          position: relative;
        }
        
        .payment-history-title {
          font-size: 15px;
          font-weight: 600;
          color: #1F2937;
        }
        
        .payment-history-table-wrapper {
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          overflow-x: auto;
        }
        
        .payment-history-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          min-width: 1000px;
        }
        
        .payment-history-table thead th {
          background: #F3F4F6;
          padding: 8px 4px;
          font-weight: 600;
          text-align: center;
          border: none;
          color: #6B7280;
          font-size: 11px;
          height: 32px;
          vertical-align: middle;
        }
        
        .payment-history-table tbody td {
          padding: 0;
          text-align: center;
          border: none;
          font-weight: 500;
          background: white;
          height: 32px;
          vertical-align: middle;
        }
        
        .payment-history-table tbody td:first-child {
          font-weight: 600;
          text-align: left;
          padding-left: 12px;
          color: white;
          position: sticky;
          left: 0;
          z-index: 1;
          border: none;
        }
        
        .payment-history-table tbody tr:nth-child(1) td:first-child {
          background: #4AA3C6;
        }
        
        .payment-history-table tbody tr:nth-child(2) td:first-child {
          background: #2B4E8C;
        }
        
        .payment-history-table tbody tr:nth-child(3) td:first-child {
          background: #8C2A35;
        }
        
        .payment-status-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 0;
          font-weight: 600;
          font-size: 12px;
          width: 100%;
          height: 32px;
          text-align: center;
          color: white;
          box-sizing: border-box;
        }
        
        .payment-status-ok {
          background: #4ADE80;
        }
        
        .payment-status-empty {
          background: transparent;
          color: #D1D5DB;
        }
        
        .payment-status-unknown {
          background: #D1D5DB;
          color: #6B7280;
        }
        
        .payment-status-no-data {
          background: white;
          color: #6B7280;
          border: none;
        }
        
        .payment-status-30 {
          background: #F59E0B;
        }
        
        .payment-status-60 {
          background: #F97316;
        }
        
        .payment-status-90 {
          background: #EF4444;
        }
        
        .payment-status-120 {
          background: #DC2626;
        }
        
        .payment-status-150 {
          background: #B91C1C;
        }
        
        .payment-status-180 {
          background: #991B1B;
        }
        
        .payment-status-code {
          background: #374151;
        }
      `}</style>
      <div className="credit-report-container">

      {/* Account Details */}
      {Object.entries(groupedAccounts).map(([key, group]) => {
        // Ensure all 3 bureaus are represented (TransUnion, Experian, Equifax)
        const allBureaus = ['TransUnion', 'Experian', 'Equifax'];
        const bureauAccountMap: Record<string, AccountInformation | null> = {};
        
        allBureaus.forEach(bureau => {
          const account = group.accounts.find(acc => acc.bureau === bureau);
          bureauAccountMap[bureau] = account || null;
        });
        
        return (
          <div key={key} className="account-section">
            {/* Creditor Header */}
            <div className="creditor-header">{group.creditorName}</div>

          {/* Account Details Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="account-table">
              <colgroup>
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th className="header-transunion">TransUnion</th>
                  <th className="header-experian">Experian</th>
                  <th className="header-equifax">Equifax</th>
                </tr>
              </thead>
              <tbody>

                <tr>
                  <td>Account #:</td>
                    {allBureaus.map((bureau) => {
                      const account = bureauAccountMap[bureau];
                      return (
                      <td key={bureau}>
                        {account ? account.accountNumber : '-'}
                        </td>
                      );
                    })}
                  </tr>

                <tr>
                  <td>Account Type:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? account.accountType : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Account Type Details:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? 'Credit Card' : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Bureau Code:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? 'Individual' : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Account Status:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? account.accountStatus : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Monthly Payment:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatCurrency(account.monthlyPayment) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Date Opened:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatDateForDisplay(account.dateOpened) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Balance:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatCurrency(account.currentBalance) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>No. of Months (terms):</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? (account.termLength || '0') : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>High Credit:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatCurrency(account.highBalance) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Credit Limit:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatCurrency(account.creditLimit) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Past Due:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatCurrency(account.amountPastDue) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Payment Status:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? (account.accountStatus === 'Derogatory' ? 'Derogatory' : 'Current') : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Last Reported:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatDateForDisplay(account.dateOpened) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Comments:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? (account.accountStatus === 'Derogatory' ? 'Derogatory account' : '-') : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Date Last Active:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatDateForDisplay(account.dateOpened) : '-'}
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td>Date of Last Payment:</td>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    return (
                      <td key={bureau}>
                        {account ? formatDateForDisplay(account.dateOpened) : '-'}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Payment History Section */}
          <div className="payment-history-section">
            <div className="payment-history-header">
              <div className="payment-history-title">Payment history</div>
              <PaymentHistoryLegend />
            </div>

            {/* Payment History Grid */}
            <div className="payment-history-table-wrapper">
              <table className="payment-history-table">
                <thead>
                  {/* Month Row */}
                  <tr>
                    <th>Month</th>
                    {(() => {
                      const firstAccount = group.accounts[0];
                      return firstAccount ? generatePaymentHistoryGrid(firstAccount).months.map((month, index) => (
                        <th key={index}>{month}</th>
                      )) : null;
                    })()}
                  </tr>
                  {/* Year Row */}
                  <tr>
                    <th>Year</th>
                    {(() => {
                      const firstAccount = group.accounts[0];
                      return firstAccount ? generatePaymentHistoryGrid(firstAccount).years.map((year, index) => (
                        <th key={index}>{year}</th>
                      )) : null;
                    })()}
                  </tr>
                </thead>
                <tbody>
                  {allBureaus.map((bureau) => {
                    const account = bureauAccountMap[bureau];
                    if (!account) {
                      // Show empty row for missing bureau data - white boxes (not present)
                      const emptyStatuses = new Array(24).fill('NO_DATA');
                      return (
                        <tr key={bureau}>
                          <td>{bureau}</td>
                          {emptyStatuses.map((status, statusIndex) => (
                            <td key={statusIndex}>
                              <span className="payment-status-badge payment-status-no-data">
                                
                              </span>
                            </td>
                          ))}
                        </tr>
                      );
                    }
                    
                    const { statuses } = generatePaymentHistoryGrid(account);
                    return (
                      <tr key={bureau}>
                        <td>{bureau}</td>
                        {statuses.map((status, statusIndex) => {
                          const getStatusClass = () => {
                            if (status === 'OK') return 'payment-status-ok';
                            if (status === '30') return 'payment-status-30';
                            if (status === '60') return 'payment-status-60';
                            if (status === '90') return 'payment-status-90';
                            if (status === '120') return 'payment-status-120';
                            if (status === '150') return 'payment-status-150';
                            if (status === '180') return 'payment-status-180';
                            if (['CO', 'RF', 'PP', 'VS'].includes(status)) return 'payment-status-code';
                            if (status === 'UNKNOWN') return 'payment-status-unknown';
                            if (status === 'NO_DATA') return 'payment-status-no-data';
                            return 'payment-status-empty';
                          };
                          
                          const getStatusDisplay = () => {
                            if (status === 'UNKNOWN') return '';
                            if (status === 'NO_DATA') return '';
                            return status;
                          };
                          
                          return (
                            <td key={statusIndex}>
                              <span className={`payment-status-badge ${getStatusClass()}`}>
                                {getStatusDisplay()}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        );
      })}
      </div>
    </div>
  );
}